import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Fade,
} from '@mui/material'
import { 
  Rocket, 
  Engineering, 
  EmojiEvents, 
  Group,
} from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules'
import { images } from '../config/images'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-coverflow'

// Custom Swiper styles
const swiperStyles = `
  .swiper-pagination-bullet {
    background: rgba(255, 0, 0, 0.3) !important;
    opacity: 1 !important;
  }
  .swiper-pagination-bullet-active {
    background: #FF0000 !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    color: #FF0000 !important;
  }
  .swiper-button-next:after,
  .swiper-button-prev:after {
    font-size: 20px !important;
    font-weight: bold !important;
  }
`

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.innerText = swiperStyles
  document.head.appendChild(styleSheet)
}

const Home: React.FC = () => {
  const [scrollY, setScrollY] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    const timer = setTimeout(() => setIsVisible(true), 500)
    
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(timer)
    }
  }, [])

  const features = [
    {
      icon: <Rocket sx={{ fontSize: 56, color: '#FF0000' }} />,
      title: 'Hybrid Propulsion',
      description: 'Developing next-generation hybrid rocket motors with advanced fuel systems and optimized performance for competitive rocketry.',
      gradient: 'linear-gradient(135deg, rgba(255,0,0,0.05) 0%, rgba(255,0,0,0.1) 100%)',
    },
    {
      icon: <Engineering sx={{ fontSize: 56, color: '#FF0000' }} />,
      title: 'Innovation Lab',
      description: 'State-of-the-art engineering solutions combining theoretical research with practical applications in aerospace technology.',
      gradient: 'linear-gradient(135deg, rgba(255,0,0,0.05) 0%, rgba(255,0,0,0.1) 100%)',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 56, color: '#FF0000' }} />,
      title: 'Global Competitions',
      description: 'Competing in prestigious rocketry contests across Mexico and the World.',
      gradient: 'linear-gradient(135deg, rgba(255,0,0,0.05) 0%, rgba(255,0,0,0.1) 100%)',
    },
    {
      icon: <Group sx={{ fontSize: 56, color: '#FF0000' }} />,
      title: 'Multidisciplinary Team',
      description: 'Passionate aerospace engineers, students, and researchers united in pushing the boundaries of rocketry technology.',
      gradient: 'linear-gradient(135deg, rgba(255,0,0,0.05) 0%, rgba(255,0,0,0.1) 100%)',
    },
  ]


  // Use gallery images from centralized config
  const galleryImages = images.gallery


  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at ${50 + scrollY * 0.02}% ${30 + scrollY * 0.01}%, rgba(255, 0, 0, 0.03) 0%, transparent 50%)`,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />
      
      {/* Floating Animation Elements */}
      <Box
        sx={{
          position: 'fixed',
          top: '10%',
          right: '10%',
          fontSize: '2rem',
          opacity: 0.1,
          transform: `translateY(${scrollY * 0.3}px) rotate(${scrollY * 0.1}deg)`,
          transition: 'all 0.1s ease-out',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        🚀
      </Box>
      <Box
        sx={{
          position: 'fixed',
          top: '60%',
          left: '5%',
          fontSize: '1.5rem',
          opacity: 0.1,
          transform: `translateY(${scrollY * -0.2}px) rotate(${scrollY * -0.05}deg)`,
          transition: 'all 0.1s ease-out',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        ⭐
      </Box>
      <Box
        sx={{
          position: 'fixed',
          top: '80%',
          right: '20%',
          fontSize: '1.8rem',
          opacity: 0.1,
          transform: `translateY(${scrollY * 0.15}px) rotate(${scrollY * 0.08}deg)`,
          transition: 'all 0.1s ease-out',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        🛰️
      </Box>
      {/* Hero Section - Image Ready */}
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${images.backgrounds.homeHero}) center/cover`,
          color: 'white',
          py: { xs: 10, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={isVisible} timeout={1000}>
            <Box>
              {/*<Typography
                variant="h1"
                component="h1"
                gutterBottom
                sx={{
                  fontSize: { xs: '3rem', md: '5rem' },
                  fontWeight: 800,
                  mb: 3,
                  color: 'white',
                  textShadow: '0 0 30px rgba(255, 255, 255, 0.5)',
                  animation: 'glow 2s ease-in-out infinite alternate',
                  '@keyframes glow': {
                    from: { textShadow: '0 0 20px rgba(255, 255, 255, 0.5)' },
                    to: { textShadow: '0 0 30px rgba(255, 107, 107, 0.8)' },
                  },
                }}
              >
                PROPULSION INDUSTRIES
              </Typography>*/}
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.5rem', md: '2.2rem' },
                  fontWeight: 300,
                  mb: 4,
                  opacity: 0.95,
                  letterSpacing: '0.5px',
                }}
              >
                🚀 Pioneering the Future of Rocket Technology
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  mb: 6,
                  maxWidth: '900px',
                  mx: 'auto',
                  opacity: 0.85,
                  lineHeight: 1.7,
                }}
              >
                From the laboratories of Mexico to international competition fields, we're pushing the boundaries 
                of rocketry with innovative technology. Join us as we reach for the stars and beyond.
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component={Link}
                  to="/projects"
                  sx={{ 
                    px: 5, 
                    py: 2, 
                    fontSize: '1.2rem',
                    boxShadow: '0 8px 32px rgba(255, 0, 0, 0.3)',
                  }}
                >
                  Explore Our Projects
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/contact"
                  sx={{
                    px: 5,
                    py: 2,
                    fontSize: '1.2rem',
                    borderColor: 'white',
                    color: 'white',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#FF6B6B',
                      color: '#FF6B6B',
                      backgroundColor: 'rgba(255, 107, 107, 0.1)',
                    },
                  }}
                >
                  Join Our Mission
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
        
        {/* Animated Rocket */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: { xs: '-10%', md: '5%' },
            fontSize: { xs: '4rem', md: '8rem' },
            opacity: 0.1,
            transform: `translateY(${scrollY * 0.5}px) rotate(${scrollY * 0.1}deg)`,
            transition: 'all 0.1s ease-out',
          }}
        >
          🚀
        </Box>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h2"
          component="h3"
          textAlign="center"
          gutterBottom
          sx={{ 
            mb: 8,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700,
            color: '#000000',
            '& span': {
              background: 'linear-gradient(45deg, #000000 30%, #FF0000 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }
          }}
        >
          <span>What Sets Us Apart</span>
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  background: feature.gradient,
                  color: '#000000', // Black text for visibility
                  border: '2px solid rgba(255,0,0,0.2)',
                  borderRadius: 4,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.02)',
                    boxShadow: '0 25px 50px rgba(255,0,0,0.25)',
                    background: 'linear-gradient(135deg, rgba(255,0,0,0.1) 0%, rgba(255,0,0,0.15) 100%)',
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ mb: 3, transform: 'scale(1)', transition: 'transform 0.3s ease' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Page Overviews Section */}
      <Box sx={{ 
        bgcolor: '#f8f9fa', 
        py: 10,
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h3"
            textAlign="center"
            gutterBottom
            sx={{ 
              mb: 8,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              color: '#000000',
            }}
          >
            Gallery Showcase
          </Typography>
          
          <Box sx={{ position: 'relative' }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
              spaceBetween={30}
              slidesPerView={1}
              centeredSlides={true}
              loop={true}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              }}
              navigation={true}
              effect="coverflow"
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 40,
                },
              }}
              style={{
                paddingBottom: '50px',
              }}
            >
              {galleryImages.map((image) => (
                <SwiperSlide key={image.id}>
                  <Card
                    sx={{
                      height: 350,
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 20px 40px rgba(255,0,0,0.2)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={image.src}
                      alt={image.alt}
                      sx={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {/*<CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {image.title}
                      </Typography>
                    </CardContent>*/}
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #FF0000 0%, #000000 100%)',
        color: 'white',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            mb: 3,
            color: 'white', // Explicit white color
          }}>
            Ready to Launch Into the Future?
          </Typography>
          <Typography variant="body1" sx={{ 
            mb: 6, 
            fontSize: '1.3rem', 
            maxWidth: '700px', 
            mx: 'auto',
            opacity: 0.9,
            lineHeight: 1.6,
            color: 'white', // Explicit white color
          }}>
            Join our mission to revolutionize rocketry technology and compete at the highest levels 
            of international aerospace innovation. The future of space exploration starts here.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/about"
              sx={{ 
                px: 5, 
                py: 2,
                borderColor: 'white',
                color: 'white',
                borderWidth: 2,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                  color: 'white',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Meet Our Team
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/contact"
              sx={{ 
                px: 5, 
                py: 2,
                borderColor: 'white',
                color: 'white',
                borderWidth: 2,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                  color: 'white',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Partner With Us
            </Button>
          </Box>
        </Container>
        
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            fontSize: '6rem',
            opacity: 0.1,
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        >
          🌟
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            fontSize: '4rem',
            opacity: 0.1,
            transform: `translateY(${scrollY * -0.2}px)`,
          }}
        >
          🚀
        </Box>
      </Box>
    </Box>
  )
}

export default Home
